import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent6b as accent, useSlide6b, Seg } from "./slide6b.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 6b — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide6b.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide6bMobile: React.FC = () => {
  const {
    meta: slide6bMeta, pairings: slide6bPairings,
    rhythm: slide6bRhythm, callout: slide6bCallout,
  } = useSlide6b();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#a06880", textTransform: "uppercase", marginBottom: 8 }}>
        {slide6bMeta.breadcrumbPre} · <span style={{ color: accent }}>{slide6bMeta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 26, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide6bMeta.titlePre}<span style={{ color: accent }}>{slide6bMeta.titleAccentWord}</span>{slide6bMeta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#8a7080", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide6bMeta.subtitle}
      </p>

      {/* shared rhythm */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6bMeta.rhythmLabel}</div>
        <p style={txt}>{renderSegs(slide6bRhythm, "#f0c9dc")}</p>
      </div>

      {/* pairings */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6bMeta.pairLabel}</div>
        {slide6bPairings.map((p, i) => (
          <div key={i} style={{
            background: p.highlight ? "rgba(199,106,148,0.08)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${p.highlight ? "rgba(199,106,148,0.3)" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 9, padding: "11px 13px", marginBottom: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontFamily: mono, color: p.highlight ? accent : "rgba(255,255,255,0.6)", letterSpacing: 1 }}>{p.side.toUpperCase()}</span>
              <span style={{ fontSize: 9, fontFamily: mono, color: "#4a6880", flexShrink: 0 }}>{p.nadi}</span>
            </div>
            <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: p.highlight ? "#f0c9dc" : "#c8dff0", marginBottom: 3 }}>{p.state}</div>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.5, color: p.highlight ? "#e3b4c9" : "#9bbdd6", margin: 0 }}>{p.desc}</p>
          </div>
        ))}
      </div>

      {/* callout */}
      <div style={{ background: "rgba(199,106,148,0.06)", border: "1px solid rgba(199,106,148,0.20)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide6bMeta.calloutLabel}</div>
        <p style={{ ...txt, color: "#e3b4c9" }}>{renderSegs(slide6bCallout, "#f7d9e8")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide6bMobile;
